import type { FilterLinksItem } from 'components/filters/filter-links/types';
import { camelCase, get, isNil, orderBy, snakeCase } from 'lodash';
import moment from 'moment';

import { getCloudinaryVideoPublicId } from './cloudinary';
import { APPROVAL_STATUSES } from './contentApproval';
import { HTML_DATE_FORMAT, isOverdue } from './date';
import { getFileSizeInUnits } from './helper/common';
import { __t, applyTranslations } from './language';
import { isCloudinaryUrl } from './url';
import { isLesserThanOrEqualTo } from './validators';

import type {
  AdminDeliverable,
  CampaignContentType,
  ChannelTypes,
  CustomReportDeliverable,
  Deliverable,
  DeliverableAnalytic,
  DeliverableAttachment,
  DeliverableAttachmentApprovalStatus,
  DeliverableAttachmentForClientApproval,
  DeliverableAttachmentType,
  DeliverableMediaStatus,
  DeliverablePersistedAttachment,
  DeliverableSortableShape,
  DeliverableTikTokAnalytic,
  DeliverableTimelineType,
  DeliverableType,
  GalleryModalItem,
  InfluencerTile,
  Subcampaign,
  SubcampaignInfluencerProfile,
  SubcampaignOffer
} from 'types';

applyTranslations(require('./i18n/deliverables-util.i18n.json'));
applyTranslations(require('modules/campaigns/components/deliverables/campaign-deliverables.i18n.json'));

export const IMAGE_MAXIMUM_SIZE = 40 * 1024 * 1024; // 40MB
export const VIDEO_MAXIMUM_SIZE = 300 * 1024 * 1024; // 300MB

export const MAX_DELIVERABLE_DESCRIPTION_LENGTH = 5000;
export const MAX_DELIVERABLE_LOCATION_LENGTH = 1024;
export const MAX_NAME_LENGTH = 65;
export const MAX_TAG_NAME_LENGTH_NEW = 65;

export const DELIVERABLE_TYPE_INSTAGRAM_POST = 'instagram_post';
export const DELIVERABLE_TYPE_INSTAGRAM_STORY = 'instagram_story';
export const DELIVERABLE_TYPE_INSTAGRAM_REEL = 'instagram_reel';
export const DELIVERABLE_TYPE_INSTAGRAM_REGRAM_STORY = 'instagram_regram_story';
export const DELIVERABLE_TYPE_INSTAGRAM_REGRAM_POST = 'instagram_regram_post';
export const DELIVERABLE_TYPE_YOUTUBE_VIDEO = 'youtube_video';
export const DELIVERABLE_TYPE_TIKTOK_VIDEO = 'tiktok_video';
export const DELIVERABLE_TYPE_ZALANDO_OUTFIT = 'zalando_outfit';
export const DELIVERABLE_TYPE_OTHER = 'other';
export const DELIVERABLE_TYPE_ENTERTAINMENT_VIDEO = 'entertainment_video';
export const DELIVERABLE_TYPE_BLOG = 'blog';
export const DELIVERABLE_TYPE_CONTENT_PRODUCTION = 'content_production';

export const CURRENT_TASK = 'Current task';
export const NEXT_TASK = 'Next task';
export const UPCOMING_TASK = 'Upcoming task';
export const TASK = 'Task';
export const INSIGHTS_PROVISION_TIMELINE = 'insightsProvisionTimeline';
export const CONTENT_GO_LIVE_TIMELINE = 'contentGoLiveTimeline';
export const CONTENT_APPROVAL_TIMELINE = 'contentApprovalTimeline';

export const DELIVERABLE_ADDITIONAL_COSTS = 'additional';
export const DELIVERABLE_ADDITIONAL_COSTS_DESCRIPTION = 'additional_costs_detail';

export const DEFAULT_DELIVERABLE_TYPE_IN_COMPOSER = DELIVERABLE_TYPE_INSTAGRAM_POST;

export const DELIVERABLE_TAB_INFORMATION = 'information';
export const DELIVERABLE_TAB_MEDIA = 'media';

export const DELIVERABLE_TAG_COLORS_MAP = {
  pink: '#e2629c',
  lavender: '#be8fd7',
  sky: '#026489',
  pool: '#4dc2ee',
  grass: '#aecc67',
  gold: '#f4c63b'
};

const ADDITIONAL_DELIVERABLE_TYPES = [
  DELIVERABLE_TYPE_INSTAGRAM_POST,
  DELIVERABLE_TYPE_INSTAGRAM_STORY,
  DELIVERABLE_TYPE_INSTAGRAM_REEL,
  DELIVERABLE_TYPE_TIKTOK_VIDEO
];

export const DELIVERABLE_TYPES = (() => [
  DELIVERABLE_TYPE_CONTENT_PRODUCTION,
  DELIVERABLE_TYPE_INSTAGRAM_POST,
  DELIVERABLE_TYPE_INSTAGRAM_STORY,
  DELIVERABLE_TYPE_INSTAGRAM_REEL,
  DELIVERABLE_TYPE_YOUTUBE_VIDEO,
  DELIVERABLE_TYPE_TIKTOK_VIDEO,
  DELIVERABLE_TYPE_BLOG,
  DELIVERABLE_TYPE_ZALANDO_OUTFIT,
  DELIVERABLE_TYPE_OTHER,
  DELIVERABLE_TYPE_ENTERTAINMENT_VIDEO
])();

export const DELIVERABLE_TYPES_CHANNELS_MAP = {
  [DELIVERABLE_TYPE_INSTAGRAM_POST]: 'instagram',
  [DELIVERABLE_TYPE_INSTAGRAM_STORY]: 'instagram',
  [DELIVERABLE_TYPE_INSTAGRAM_REEL]: 'instagram',
  [DELIVERABLE_TYPE_TIKTOK_VIDEO]: 'tiktok',
  [DELIVERABLE_TYPE_YOUTUBE_VIDEO]: 'youtube'
};

export const DELIVERABLE_ATTACHMENT_TYPE_HIGHRES = 'highres';
export const DELIVERABLE_ATTACHMENT_TYPE_ANALYTICS = 'analytics';
export const DELIVERABLE_ATTACHMENT_TYPE_CONTENT = 'content';

export const SUPPORTED_DELIVERABLE_TYPES = (() => [
  DELIVERABLE_TYPE_CONTENT_PRODUCTION,
  DELIVERABLE_TYPE_INSTAGRAM_POST,
  DELIVERABLE_TYPE_INSTAGRAM_STORY,
  DELIVERABLE_TYPE_INSTAGRAM_REEL,
  DELIVERABLE_TYPE_YOUTUBE_VIDEO,
  DELIVERABLE_TYPE_TIKTOK_VIDEO,
  DELIVERABLE_TYPE_OTHER,
  DELIVERABLE_TYPE_BLOG,
  DELIVERABLE_TYPE_ZALANDO_OUTFIT,
  DELIVERABLE_TYPE_ENTERTAINMENT_VIDEO
])();

export const DELIVERABLE_ICONS = {
  contentProduction: 'magic-wand-custom-fg',
  instagramPost: 'instagram-custom-fg',
  instagramRegramPost: 'instagram-custom-fg',
  instagramStory: 'story-custom-fg',
  instagramRegramStory: 'story-custom-fg',
  instagramReel: 'reel-custom-fg',
  youtubeVideo: 'youtube-custom-fg',
  tiktokVideo: 'tiktok-custom-fg',
  zalandoOutfit: 'hanger-custom-fg',
  other: 'star-custom-fg',
  blog: 'blog-custom-fg',
  entertainmentVideo: 'equalizer-custom-bg'
};

export const DELIVERABLE_POSTPONE_ACTION = 'postpone';
export const DELIVERABLE_ADD_HASHTAGS_ACTION = 'add_hashtags';
export const DELIVERABLE_REPLACE_HASHTAGS_ACTION = 'replace_hashtags';
export const DELIVERABLE_ADD_TAGS_ACTION = 'add_tags';
export const DELIVERABLE_REPLACE_TAGS_ACTION = 'replace_tags';
export const DELIVERABLE_ADD_MENTIONS_ACTION = 'add_mentions';
export const DELIVERABLE_REPLACE_MENTIONS_ACTION = 'replace_mentions';
export const DELIVERABLE_REPLACE_COLORED_TAG_ACTION = 'replace_colored_tag';
export const DELIVERABLE_REPLACE_TIMELINE_ACTION = 'replace_timeline';
export const DELIVERABLE_REPLACE_DESCRIPTION_ACTION = 'replace_description';
export const DELIVERABLE_ADD_WMC_LINK_ACTION = 'add_wmc_link';
export const DELIVERABLE_REPLACE_WMC_LINK_ACTION = 'replace_wmc_link';
export const DELIVERABLE_ADD_REPLACE_ZALANDO_PAGE_ACTION = 'add_replace_zalando_page';
export const DELIVERABLE_ADD_ZALANDO_PAGE_ACTION = 'add_zalando_page';
export const DELIVERABLE_REPLACE_ZALANDO_PAGE_ACTION = 'replace_zalando_page';

export const DELIVERABLE_EDIT_ACTIONS = [
  DELIVERABLE_POSTPONE_ACTION,
  DELIVERABLE_REPLACE_TIMELINE_ACTION,
  DELIVERABLE_REPLACE_COLORED_TAG_ACTION,
  DELIVERABLE_REPLACE_DESCRIPTION_ACTION,
  DELIVERABLE_ADD_REPLACE_ZALANDO_PAGE_ACTION,
  DELIVERABLE_ADD_ZALANDO_PAGE_ACTION,
  DELIVERABLE_REPLACE_ZALANDO_PAGE_ACTION,
  DELIVERABLE_ADD_HASHTAGS_ACTION,
  DELIVERABLE_REPLACE_HASHTAGS_ACTION,
  DELIVERABLE_ADD_TAGS_ACTION,
  DELIVERABLE_REPLACE_TAGS_ACTION,
  DELIVERABLE_ADD_MENTIONS_ACTION,
  DELIVERABLE_REPLACE_MENTIONS_ACTION
];

export const DELIVERABLE_EDIT_ACTIONS_ZALANDO_OUTFIT = [
  DELIVERABLE_POSTPONE_ACTION,
  DELIVERABLE_REPLACE_TIMELINE_ACTION,
  DELIVERABLE_REPLACE_COLORED_TAG_ACTION,
  DELIVERABLE_REPLACE_DESCRIPTION_ACTION
];

export const DELIVERABLE_ACTION_HASHTAGS = 'hashtags';
export const DELIVERABLE_ACTION_TAGS = 'tags';
export const DELIVERABLE_ACTION_MENTIONS = 'mentions';

export const SUPPORTED_MIMETYPES = ['image/jpeg',
  'image/png',
  'image/gif',
  'image/bmp',
  'image/tiff',
  'application/mp4',
  'video/3gpp',
  'video/3gpp2',
  'video/x-msvideo',
  'video/mp4',
  'video/quicktime',
  'video/ogg',
  'video/webm',
  'video/x-flv',
  'video/x-ms-wmv',
  'video/mpeg',
  'video/avi',
  'text/plain',
  'text/csv',
  'application/pdf'];

export const CONTENT_APPROVAL_SUPPORTED_TASK_TYPES = (() => [
  DELIVERABLE_TYPE_INSTAGRAM_POST,
  DELIVERABLE_TYPE_INSTAGRAM_STORY,
  DELIVERABLE_TYPE_INSTAGRAM_REEL,
  DELIVERABLE_TYPE_TIKTOK_VIDEO,
  DELIVERABLE_TYPE_YOUTUBE_VIDEO,
  DELIVERABLE_TYPE_ZALANDO_OUTFIT,
  DELIVERABLE_TYPE_ENTERTAINMENT_VIDEO
])();

export const isZalandoOutfitPresent = (
  deliverables: Array<Deliverable | CustomReportDeliverable>
): boolean => (
  deliverables.some(d => (
    d.deliverableType === DELIVERABLE_TYPE_ZALANDO_OUTFIT
  ))
);

export const isZalandoOutfitOnly = (
  deliverables: Array<Deliverable | CustomReportDeliverable>
): boolean => (
  deliverables.every(d => (
    d.deliverableType === DELIVERABLE_TYPE_ZALANDO_OUTFIT
  ))
);

export const isEntertainmentVideoOnly = (
  deliverables: Array<Deliverable | CustomReportDeliverable>
): boolean => (
  deliverables.every(d => (
    d.deliverableType === DELIVERABLE_TYPE_ENTERTAINMENT_VIDEO
  ))
);

export const getContentApprovalSupportedTaskTypes = (
  isSCP: boolean = false
): Array<string> => {
  const taskTypes = CONTENT_APPROVAL_SUPPORTED_TASK_TYPES;

  if (isSCP) {
    taskTypes.push(DELIVERABLE_TYPE_ZALANDO_OUTFIT);
  }

  return taskTypes;
};

export function isRegramContent(deliverableType: string) {
  return [DELIVERABLE_TYPE_INSTAGRAM_REGRAM_POST, DELIVERABLE_TYPE_INSTAGRAM_REGRAM_STORY].includes(deliverableType);
}

export function isInstagramPost(deliverableType: string) {
  return [DELIVERABLE_TYPE_INSTAGRAM_REGRAM_POST, DELIVERABLE_TYPE_INSTAGRAM_POST].includes(deliverableType);
}

export function isInstagramStory(deliverableType: string) {
  return [DELIVERABLE_TYPE_INSTAGRAM_REGRAM_STORY, DELIVERABLE_TYPE_INSTAGRAM_STORY].includes(deliverableType);
}

export function isInstagramReel(deliverableType: string) {
  return deliverableType === DELIVERABLE_TYPE_INSTAGRAM_REEL;
}

export function isEntertainmentVideo(deliverableType: string) {
  return deliverableType === DELIVERABLE_TYPE_ENTERTAINMENT_VIDEO;
}

export function getModifiedDeliverable(deliverables: Array<Deliverable>): Array<Deliverable> {
  return deliverables.map(deliverable => {
    if (deliverable.regram) {
      const deliverableType = deliverable.deliverableType === DELIVERABLE_TYPE_INSTAGRAM_POST ? DELIVERABLE_TYPE_INSTAGRAM_REGRAM_POST : DELIVERABLE_TYPE_INSTAGRAM_REGRAM_STORY;

      return { ...deliverable, deliverableType };
    }

    return deliverable;
  });
}

export function isDeliverableSupported(deliverable: Deliverable): boolean {
  return SUPPORTED_DELIVERABLE_TYPES.includes(deliverable.deliverableType);
}

export function validateFile(file: File, videoMaxSize = VIDEO_MAXIMUM_SIZE): string | undefined | null {
  const isVideo = file.type.indexOf('video') >= 0;
  const maxSize = (isVideo ? videoMaxSize : IMAGE_MAXIMUM_SIZE);
  const validators = [
    isLesserThanOrEqualTo(
      file.size,
      maxSize,
      __t('deliverables_util.file_max_size_exceeded', {
        maxSize: getFileSizeInUnits(maxSize, 'mb')
      })
    )
  ];

  const invalid = validators.find(v => !v.valid);
  return invalid ? invalid.message : null;
}

export function getInfluencerAvailableDeliverableChannels(influencer?: SubcampaignInfluencerProfile | null): Array<string> {
  const channels = influencer && influencer.channels || {};
  return Object.keys(channels).filter(channelName => !isNil(channels[channelName]));
}

export function getInfluencersAvailableDeliverableChannels(influencers: Array<SubcampaignInfluencerProfile> = []): Array<string> {
  if (!influencers.length) {
    return [];
  }

  return ['instagram', 'youtube', 'blog', 'tiktok']
    .filter(channelName => influencers.every(i => i.channels && !isNil(i.channels[channelName])));
}

export function getAvailableDeliverableTypesForChannels(channels: Array<string>, isEntertainmentVideoEnabled: boolean, isNewDeliverable?: boolean): Array<DeliverableType> {
  let defaultTypes = isNewDeliverable
    ? [DELIVERABLE_TYPE_OTHER, DELIVERABLE_TYPE_ZALANDO_OUTFIT, DELIVERABLE_TYPE_ENTERTAINMENT_VIDEO]
    : [DELIVERABLE_TYPE_OTHER, DELIVERABLE_TYPE_CONTENT_PRODUCTION];

  if (!channels.includes('instagram')) {
    defaultTypes = defaultTypes.filter(type => type !== DELIVERABLE_TYPE_ZALANDO_OUTFIT);
  }

  if (!isEntertainmentVideoEnabled) {
    defaultTypes = defaultTypes.filter(type => type !== DELIVERABLE_TYPE_ENTERTAINMENT_VIDEO);
  }

  return (DELIVERABLE_TYPES)
    .filter(type => channels.some(c => type.startsWith(c)) || defaultTypes.includes(type));
}

export function canInfluencerProvideSuchDeliverables(influencer: SubcampaignInfluencerProfile, deliverables: Array<Deliverable>, isEntertainmentVideoEnabled: boolean, isNewCampaign?: boolean): boolean {
  if (deliverables.length === 0) {
    return true;
  }

  const influencerChannels = getInfluencerAvailableDeliverableChannels(influencer);
  const influencerDeliverableTypes = getAvailableDeliverableTypesForChannels(influencerChannels, isEntertainmentVideoEnabled, isNewCampaign);

  return deliverables.every(d => influencerDeliverableTypes.includes(d.deliverableType));
}

export function filterOutIrrelevantDeliverables(
  deliverables: Array<Deliverable>,
  influencers: Array<SubcampaignInfluencerProfile>,
  isSCP: boolean
): Array<Deliverable> {
  const availableChannels = getInfluencersAvailableDeliverableChannels(influencers);

  return deliverables.filter(deliverable =>
    availableChannels.includes(deliverable.deliverableType.split('_')[0])
    || deliverable.deliverableType === DELIVERABLE_TYPE_OTHER
    || deliverable.deliverableType === DELIVERABLE_TYPE_CONTENT_PRODUCTION
    || deliverable.deliverableType === DELIVERABLE_TYPE_ENTERTAINMENT_VIDEO
    || (isSCP && deliverable.deliverableType === DELIVERABLE_TYPE_ZALANDO_OUTFIT)
  );
}

export function filterAttachmentsByType(
  attachments: Array<DeliverableAttachment> | undefined | null,
  attachmentType: DeliverableAttachmentType
): Array<DeliverableAttachment> {
  return (attachments || []).filter(a => a.meta && a.meta.type === attachmentType);
}

type FilterableDeliverableShape = {
  contentApprovalTimeline: {
    status?: string
  },
  insightsProvisionTimeline: {
    status?: string
  }
}

const iterateesBySortingType = {
  start_date_asc: [
    d => new Date(d.startDate),
    d => new Date(d.contentApprovalTimeline?.startDate),
    d => new Date(d.contentGoLiveTimeline?.startDate),
    d => new Date(d.insightsProvisionTimeline?.startDate),
    d => d.name,
    'id'
  ],
  end_date_asc: [
    d => new Date(d.endDate),
    d => new Date(d.insightsProvisionTimeline?.endDate),
    d => new Date(d.contentGoLiveTimeline?.endDate),
    d => new Date(d.contentApprovalTimeline?.endDate),
    d => d.name,
    'id'
  ]
};

const orderBySortingType = {
  start_date_asc: 'asc',
  end_date_asc: 'desc'
};

export function sortNewDeliverables<T>(deliverables: Array<T> | null | undefined, sort: string): Array<T> {
  const iteratees = iterateesBySortingType[sort];
  return orderBy(deliverables, iteratees, orderBySortingType[sort]);
}

export function sortDeliverables<T extends DeliverableSortableShape>(deliverables: Array<T> | null| undefined): Array<T> {
  const iteratees = [d => new Date(d.startDate), 'id'];
  return orderBy(deliverables, iteratees, ['asc', 'asc']);
}

export function filterDeliverables<T extends FilterableDeliverableShape>(deliverables: Array<T> | null | undefined, filter: string): Array<T> {
  if (filter === 'overdue_content') {
    return (deliverables ?? []).filter(d => d.contentApprovalTimeline?.status === 'overdue');
  }

  if (filter === 'overdue_insights') {
    return (deliverables ?? []).filter(d => d.insightsProvisionTimeline?.status === 'overdue');
  }

  return deliverables ?? [];
}

function getHighresAttachments(deliverable) {
  return filterAttachmentsByType(
    deliverable.postAttachments,
    DELIVERABLE_ATTACHMENT_TYPE_HIGHRES
  );
}

function getAnalyticsAttachments(deliverable) {
  return filterAttachmentsByType(
    deliverable.postAttachments,
    DELIVERABLE_ATTACHMENT_TYPE_ANALYTICS
  );
}

function getDeliverableMediaFallbackStatus(deliverable: Deliverable): DeliverableMediaStatus {
  return isOverdue(deliverable.endDate) ? 'incomplete' : 'ok';
}

function getApprovableContentProductionMediaStatus(deliverable: Deliverable, isForBrand: boolean): DeliverableMediaStatus {
  const highresAttachments = getHighresAttachments(deliverable);
  const hasHighresAttachments = highresAttachments.length > 0;
  const hasAttachmentsForReview = highresAttachments.some(a => a.approvalStatus !== 'approved' && a.approvalStatus !== 'declined');

  if (!isForBrand) {
    if (!hasHighresAttachments) {
      return getDeliverableMediaFallbackStatus(deliverable);
    }
    if (hasAttachmentsForReview) {
      return 'pending';
    }
  } else {
    if (!hasHighresAttachments || highresAttachments.every(a => a.approvalStatus === 'declined')) {
      return 'pending';
    }
    if (hasAttachmentsForReview) {
      return 'incomplete';
    }
  }
  return 'complete';
}

function getInstagramPostDeliverableMediaStatus(deliverable: Deliverable): DeliverableMediaStatus {
  const hasPost = Boolean(deliverable.post);
  const hasHighresAttachments = getHighresAttachments(deliverable).length > 0;
  const hasAnalyticsAttachments = getAnalyticsAttachments(deliverable).length > 0;

  if (hasPost && hasHighresAttachments && hasAnalyticsAttachments) {
    return 'complete';
  }

  return getDeliverableMediaFallbackStatus(deliverable);
}

function getInstagramStoryDeliverableMediaStatus(deliverable: Deliverable): DeliverableMediaStatus {
  const hasHighresAttachments = getHighresAttachments(deliverable).length > 0;
  const hasAnalyticsAttachments = getAnalyticsAttachments(deliverable).length > 0;

  if (hasHighresAttachments && hasAnalyticsAttachments) {
    return 'complete';
  }

  return getDeliverableMediaFallbackStatus(deliverable);
}

function getTikTokVideoDeliverableMediaStatus(deliverable: Deliverable): DeliverableMediaStatus {
  const hasPost = Boolean(deliverable.post);
  const hasAnalyticsAttachments = getAnalyticsAttachments(deliverable).length > 1;

  if (hasPost && hasAnalyticsAttachments) {
    return 'complete';
  }

  return getDeliverableMediaFallbackStatus(deliverable);
}

function getBlogOrYoutubeDeliverableMediaStatus(deliverable: Deliverable): DeliverableMediaStatus {
  const hasPost = Boolean(deliverable.post);
  if (hasPost) {
    return 'complete';
  }

  return getDeliverableMediaFallbackStatus(deliverable);
}

export function getDeliverableMediaStatus(deliverable: Deliverable, isForBrand: boolean): DeliverableMediaStatus {
  switch (deliverable.deliverableType) {
    case DELIVERABLE_TYPE_CONTENT_PRODUCTION:
      return getApprovableContentProductionMediaStatus(deliverable, isForBrand);
    case DELIVERABLE_TYPE_INSTAGRAM_STORY:
      return getInstagramStoryDeliverableMediaStatus(deliverable);
    case DELIVERABLE_TYPE_INSTAGRAM_POST:
      return getInstagramPostDeliverableMediaStatus(deliverable);
    case DELIVERABLE_TYPE_TIKTOK_VIDEO:
      return getTikTokVideoDeliverableMediaStatus(deliverable);
    case DELIVERABLE_TYPE_BLOG:
    case DELIVERABLE_TYPE_YOUTUBE_VIDEO:
      return getBlogOrYoutubeDeliverableMediaStatus(deliverable);
    case DELIVERABLE_TYPE_OTHER:
      return 'ok';
    default:
      return getDeliverableMediaFallbackStatus(deliverable);
  }
}

export function generateNewDeliverableId() {
  return Math.random() - 1; // new deliverable id's are always negative, existing ones are positive
}

export function isDeliverableNew(deliverable: Deliverable) {
  return deliverable.id < 0; // new deliverable id's are always negative, existing ones are positive
}

export function prepareDeliverableAttachmentsForGallery(attachments: Array<any>, useAssetDirectly?: boolean): Array<GalleryModalItem> {
  return attachments.map(attachment => {
    if (attachment.assetType === 'video' || attachment.mediaType === 'video') {
      return ({
        id: attachment.id,
        type: isCloudinaryUrl(attachment.url) ? 'cloudinary-video' : 'collabary-video',
        assetUrl: attachment.url,
        thumbnailUrl: attachment.thumbnailUrl,
        url: attachment.url,
        isSelected: attachment.isSelected || false,
        cloudinaryVideoId: get(attachment, 'meta.cloudinary.publicId', getCloudinaryVideoPublicId(attachment.url)),
        useAssetDirectly
      });
    }

    return ({
      id: attachment.id,
      type: 'cloudinary-image',
      assetUrl: attachment.url,
      thumbnailUrl: attachment.thumbnailUrl,
      url: attachment.url,
      isSelected: attachment.isSelected || false,
      useAssetDirectly
    });
  });
}

export function encodeFileNameInZipArchive(name: string) {
  return name.replace('/', '|');
}

export function buildPrefixForDownloadableAttachment(
  creatorProfile: SubcampaignInfluencerProfile | undefined | null,
  deliverableId: number,
  deliverableType: DeliverableType
): string {
  return `Collabary${creatorProfile ? ` - ${encodeFileNameInZipArchive(creatorProfile.name)}` : ''} - ${__t(`deliverables_util.task_types.${deliverableType}`)} #${deliverableId}`;
}

export function buildPrefixForDownloadableCampaignContent(
  campaignId: number | undefined | null,
  campaignName: string,
  creatorProfile?: InfluencerTile | SubcampaignInfluencerProfile | null
): string {
  const campaignIdPrefix = campaignId ? `#${campaignId} ` : '';
  const encodedCampaignName = encodeFileNameInZipArchive(campaignName);
  const creatorProfileSuffix = creatorProfile ? ` - ${encodeFileNameInZipArchive(creatorProfile.name)}` : '';

  return `Collabary campaign ${campaignIdPrefix}- ${encodedCampaignName}${creatorProfileSuffix}`;
}

export function getDeliverablesCountByTypeFromOffer(offer: SubcampaignOffer | undefined | null, type: DeliverableType | string): number {
  const items = get(offer, 'deliverablesOverview') || {};
  const item = items[camelCase(type)] || [];

  return item.length || 0;
}

export function getDeliverablesCountFromOffer(offer?: SubcampaignOffer | null): number {
  return get(offer, 'deliverablesOverview.totalCount') || 0;
}

export function isContentReviewed(attachment: DeliverableAttachmentForClientApproval) {
  const approvalStatus = attachment.approvalStatus;
  return approvalStatus === 'approved' || approvalStatus === 'declined';
}

export const scrollToDeliverable = (deliverableId: number, customSelectorText?: string): void => {
  const selector = customSelectorText
    ? `#${customSelectorText}-${deliverableId}`
    : `#deliverable-${deliverableId}`;

  const scrollToRef = document.querySelector(selector);

  if (scrollToRef) {
    scrollToRef.scrollIntoView();
  }
};

export function getUniqDeliverableTypes(deliverableTypes: Array<DeliverableType>): Set<ChannelTypes> {
  const uniqTypes = new Set<ChannelTypes>();

  deliverableTypes.forEach((deliverableType) => {
    if (deliverableType.startsWith('instagram')) {
      uniqTypes.add('instagram');
    }

    if (deliverableType.startsWith('youtube')) {
      uniqTypes.add('youtube');
    }

    if (deliverableType.startsWith('tiktok')) {
      uniqTypes.add('tiktok');
    }
  });

  return uniqTypes;
}

export const canDeliverableTypesBeMixed = (deliverableTypeA: DeliverableType, deliverableTypeB: DeliverableType): boolean => {
  const uniqTypes = getUniqDeliverableTypes([deliverableTypeA, deliverableTypeB]);
  const hasYT = uniqTypes.has('youtube');

  return !hasYT || (hasYT && uniqTypes.size === 1);
};

export const canAddDeliverableToOffer = (deliverableType: DeliverableType, offer: SubcampaignOffer): boolean => {
  const items = get(offer, 'deliverablesOverview') || {};
  const deliverableTypes = Object.keys(items)
    .filter(key => items[key].length > 0)
    .map(key => snakeCase(key));

  return (deliverableTypes)
    .every(type => canDeliverableTypesBeMixed(deliverableType, type));
};

export const areDeliverableTypesAlreadyMixed = (deliverableTypes: Array<DeliverableType>): boolean => {
  const uniqTypes = getUniqDeliverableTypes(deliverableTypes);
  const hasAllPossibleTypes = uniqTypes.size === 3;
  const hasMixedWithYT = uniqTypes.size === 2 && uniqTypes.has('youtube');

  return hasAllPossibleTypes || hasMixedWithYT;
};

export function getOffersUniqTypes(offers: Array<SubcampaignOffer>): Set<ChannelTypes> {
  const uniqTypes = new Set<ChannelTypes>();

  offers.forEach((offer) => {
    const items = get(offer, 'deliverablesOverview') || {};

    const deliverableTypes = Object.keys(items)
      .filter(key => items[key].length > 0)
      .map(key => snakeCase(key));

    getUniqDeliverableTypes(deliverableTypes)
      .forEach((value) => uniqTypes.add(value));
  });

  return uniqTypes;
}

export function getCampaignContentType(offers: Array<SubcampaignOffer>): CampaignContentType {
  const uniqTypes = getOffersUniqTypes(offers);

  if (uniqTypes.size === 1 && uniqTypes.has('instagram')) {
    return 'instagram';
  }

  if (uniqTypes.size === 1 && uniqTypes.has('youtube')) {
    return 'youtube';
  }

  if (uniqTypes.size === 1 && uniqTypes.has('tiktok')) {
    return 'tiktok';
  }

  return 'mixed';
}

export const areDeliverablesAlreadyMixedInOffers = (offers: Array<SubcampaignOffer>): boolean => {
  const uniqTypes = getOffersUniqTypes(offers);
  const hasAllPossibleTypes = uniqTypes.size === 3;
  const hasMixedWithYT = uniqTypes.size === 2 && uniqTypes.has('youtube');

  return hasAllPossibleTypes || hasMixedWithYT;
};

export const areDeliverablesAlreadyMixedInSubcampaigns = (subcampaigns: Array<Subcampaign>): boolean => {
  const subcampaignOfferFilter = (v): v is SubcampaignOffer => Boolean(v);

  return areDeliverablesAlreadyMixedInOffers([
    ...subcampaigns.map(s => s.offer).filter(subcampaignOfferFilter),
    ...subcampaigns.map(s => s.updatedOffer).filter(subcampaignOfferFilter)
  ]);
};

export const canAddDeliverableToSubcampaigns = (
  deliverableType: DeliverableType,
  currentSubcampaignDeliverables: Array<Deliverable>,
  otherSubcampaignList: Array<Subcampaign>
) => {
  // can it be added to the current subcampaign's deliverables?
  const canBeAddedToCurrentDeliverableList = currentSubcampaignDeliverables
    .every(d => canDeliverableTypesBeMixed(deliverableType, d.deliverableType));

  // can it be added to the campaign along with other subcampaign's deliverables?
  const canBeAddedConsideringOtherSubcampaigns = otherSubcampaignList
    .every(s =>
      canAddDeliverableToOffer(deliverableType, s.offer) && // normal offer
      (s.updatedOffer ? canAddDeliverableToOffer(deliverableType, s.updatedOffer) : true) // renegotiations case
    );

  return canBeAddedToCurrentDeliverableList && canBeAddedConsideringOtherSubcampaigns ||
    areDeliverableTypesAlreadyMixed(currentSubcampaignDeliverables.map(d => d.deliverableType)) ||
    areDeliverablesAlreadyMixedInSubcampaigns(otherSubcampaignList);
};

export function getTikTokAnalytics(analytics: Array<DeliverableAnalytic | DeliverableTikTokAnalytic>): Array<DeliverableTikTokAnalytic> {
  return analytics.reduce<Array<DeliverableTikTokAnalytic>>((res, x) => {
    if (x.metricsType === 'tiktok_video_metrics') {
      return [...res, x];
    }

    return res;
  }, []);
}

export function getInstagramAnalytics(analytics: Array<DeliverableAnalytic | DeliverableTikTokAnalytic>): Array<DeliverableAnalytic> {
  return analytics.reduce<Array<DeliverableAnalytic>>((res, x) => {
    if (x.metricsType !== 'tiktok_video_metrics') {
      return [...res, x];
    }

    return res;
  }, []);
}

export function getInsightsProvisionStartDate(deliverableType: DeliverableType, goLiveContentEndDate: string): string {
  if (!(deliverableType && goLiveContentEndDate)) {
    return '';
  }

  let numberOfDaysAfterGoLiveEndDate;
  switch (deliverableType) {
    case DELIVERABLE_TYPE_TIKTOK_VIDEO:
      numberOfDaysAfterGoLiveEndDate = 9;
      break;
    case DELIVERABLE_TYPE_YOUTUBE_VIDEO:
      numberOfDaysAfterGoLiveEndDate = 3;
      break;
    default:
      numberOfDaysAfterGoLiveEndDate = 2;
      break;
  }

  return moment(goLiveContentEndDate).add(numberOfDaysAfterGoLiveEndDate, 'd').format(HTML_DATE_FORMAT);
}

export function getDeliverableTimelineStatusType(
  timeline: DeliverableTimelineType,
  isForBrand?: boolean): 'hidden' | 'highlight' | 'normal' {
  if (!timeline.status) {
    return 'hidden';
  }

  if (timeline.status === 'brand_overdue') {
    return isForBrand ? 'highlight' : 'normal';
  }

  if (timeline.status === 'overdue') {
    return 'highlight';
  }

  if (timeline.status === 'in_review') {
    return 'normal';
  }

  return 'hidden';
}

export function isHighresImagesRequired(requiresApproval: boolean, deliverableType: DeliverableType) {
  return (!requiresApproval &&
    [
      DELIVERABLE_TYPE_INSTAGRAM_POST,
      DELIVERABLE_TYPE_INSTAGRAM_REEL,
      DELIVERABLE_TYPE_TIKTOK_VIDEO,
      DELIVERABLE_TYPE_YOUTUBE_VIDEO
    ].includes(deliverableType))
    || [DELIVERABLE_TYPE_INSTAGRAM_STORY, DELIVERABLE_TYPE_BLOG, DELIVERABLE_TYPE_OTHER].includes(deliverableType);
}

export function isAnalyticsScreenshotRequired(deliverableType: DeliverableType) {
  return ![DELIVERABLE_TYPE_BLOG, DELIVERABLE_TYPE_OTHER, DELIVERABLE_TYPE_YOUTUBE_VIDEO].includes(deliverableType);
}

export function isFeedbackProvided(status: DeliverableAttachmentApprovalStatus) {
  return [APPROVAL_STATUSES.APPROVED, APPROVAL_STATUSES.DECLINED].includes(status);
}

export const getDeliverablesSelectViewItems = (
  itemsToIgnore?: Array<string>
): Array<FilterLinksItem> => {
  const items = ['information', 'content_approval', 'insights']
    .filter(item => !(itemsToIgnore || []).includes(item));

  return items.map(item => ({
    identifier: item,
    label: __t(`campaign_deliverables.view_filter.${item}`)
  }));
};

export const getDeliverablesExpandStateItems = (): Array<FilterLinksItem> => {
  const items = ['expand', 'collapse'];

  return items.map(item => ({
    identifier: item,
    label: __t(`deliverables_util.expand_state.${item}`)
  }));
};

export const getDeliverableSelectViewItemsToIgnore = (
  deliverables: Array<Deliverable>
): Array<string> => {
  const containsContentApprovalTask = deliverables.some(deliverable => (
    !isNil(deliverable.contentApprovalTimeline.status))
  );

  if (isZalandoOutfitOnly(deliverables) || isEntertainmentVideoOnly(deliverables)) {
    return ['insights'];
  }

  return !containsContentApprovalTask ? ['content_approval'] : [];
};

export const isPersistedAttachment = (
  param: {
    id: number | string,
    status?: DeliverableAttachmentApprovalStatus
  }
): param is DeliverablePersistedAttachment => (
  typeof param.id === 'number'
);

export const hasCaptionBeenUpdated = (
  oldDeliverable: Deliverable,
  currentDeliverable: Deliverable,
  isForBrand: boolean | undefined
): boolean => {
  if (isForBrand) {
    return false;
  }

  const oldCaptionStatus = oldDeliverable.contentApproval?.caption?.approvalStatus;
  const currentCaptionStatus = currentDeliverable?.contentApproval?.caption?.approvalStatus;

  return oldCaptionStatus !== currentCaptionStatus && oldCaptionStatus === 'pending';
};

export const getAdditionalDeliverablePossibleTaskTypes = (deliverables: Array<AdminDeliverable | Deliverable>): Array<DeliverableType> => {
  const taskTypes = new Set() as Set<DeliverableType>;

  deliverables.forEach(deliverable => {
    if (ADDITIONAL_DELIVERABLE_TYPES.includes(deliverable.deliverableType)) {
      taskTypes.add(deliverable.deliverableType);

      if (deliverable.deliverableType === DELIVERABLE_TYPE_INSTAGRAM_POST) {
        taskTypes.add(DELIVERABLE_TYPE_INSTAGRAM_REGRAM_POST);
      }

      if (deliverable.deliverableType === DELIVERABLE_TYPE_INSTAGRAM_STORY) {
        taskTypes.add(DELIVERABLE_TYPE_INSTAGRAM_REGRAM_STORY);
      }
    }
  });

  return Array.from(taskTypes);
};
